import { useCallback } from "react"
import { apiFetch } from "../../../utils/apiFetch"

export default function useAgendaAppointments({
  API_BASE,
  clinicId,
  date,
  selectedTreatment,
  selectedHeldEvent,
  selectedAppointment,
  newAppt,
  setSaving,
  setShowNewModal,
  setDoctorSuggestion,
  setAlternatives,
  setSelectedHeldEvent,
  setSelectedAppointment,
  setDate,
  loadHeldSlots,
  loadAgenda,
  onPlanItemLinked,
  isReadOnly,
  readOnlyReason
}) {
  const refreshAgendaView = useCallback(
    async (targetClinicId = clinicId, targetDate = date) => {
      if (!targetClinicId) return

      const freshHeld = await loadHeldSlots(targetClinicId, targetDate)
      await loadAgenda(targetClinicId, targetDate, freshHeld)
    },
    [clinicId, date, loadHeldSlots, loadAgenda]
  )

  const createAppointment = useCallback(async () => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return
    }

    if (!clinicId || !newAppt.patientId || !newAppt.start || !newAppt.treatmentId) {
      alert("Completa paciente, tratamiento y fecha")
      return
    }

    try {
      setSaving(true)

      const start = new Date(newAppt.start)
      const end = new Date(start)
      end.setMinutes(end.getMinutes() + Number(newAppt.duration || 30))

      const res = await apiFetch(`${API_BASE}/appointments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          clinicId,
          patientId: newAppt.patientId,
          doctorId: newAppt.doctorId || undefined,
          chairId: newAppt.chairId || undefined,
          treatmentId: newAppt.treatmentId,
          treatmentPlanItemId: newAppt.treatmentPlanItemId || undefined,
          startTime: start.toISOString(),
          endTime: end.toISOString(),
          reason: selectedTreatment?.name || "Cita manual inteligente"
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo crear la cita")
        return
      }

      if (newAppt.treatmentPlanItemId && onPlanItemLinked) {
        await onPlanItemLinked(newAppt.patientId)
      }

      alert("Cita creada correctamente")
      setShowNewModal(false)
      setDoctorSuggestion(null)
      setAlternatives([])

      await refreshAgendaView()
    } catch {
      alert("Error al crear la cita")
    } finally {
      setSaving(false)
    }
  }, [
    isReadOnly,
    readOnlyReason,
    API_BASE,
    clinicId,
    newAppt,
    selectedTreatment,
    setSaving,
    setShowNewModal,
    setDoctorSuggestion,
    setAlternatives,
    refreshAgendaView,
    onPlanItemLinked
  ])

  const holdAppointment = useCallback(async () => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return
    }

    if (!clinicId || !newAppt.patientId || !newAppt.start || !newAppt.doctorId || !newAppt.treatmentId) {
      alert("Para reservar temporalmente necesitas paciente, tratamiento, hora y doctor")
      return
    }

    try {
      setSaving(true)

      const start = new Date(newAppt.start)
      const end = new Date(start)
      end.setMinutes(end.getMinutes() + Number(newAppt.duration || 30))

      const res = await apiFetch(`${API_BASE}/appointments/hold-appointment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          clinicId,
          patientId: newAppt.patientId,
          doctorId: newAppt.doctorId,
          chairId: newAppt.chairId || undefined,
          treatmentId: newAppt.treatmentId,
          treatmentPlanItemId: newAppt.treatmentPlanItemId || undefined,
          startTime: start.toISOString(),
          endTime: end.toISOString(),
          reason: selectedTreatment?.name || "Reserva temporal desde agenda"
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo crear la reserva")
        return
      }

      if (newAppt.treatmentPlanItemId && onPlanItemLinked) {
        await onPlanItemLinked(newAppt.patientId)
      }

      alert("Reserva temporal creada")
      setShowNewModal(false)
      setDoctorSuggestion(null)
      setAlternatives([])

      await refreshAgendaView()
    } catch {
      alert("Error al reservar temporalmente")
    } finally {
      setSaving(false)
    }
  }, [
    isReadOnly,
    readOnlyReason,
    API_BASE,
    clinicId,
    newAppt,
    selectedTreatment,
    setSaving,
    setShowNewModal,
    setDoctorSuggestion,
    setAlternatives,
    refreshAgendaView,
    onPlanItemLinked
  ])

  const confirmHeld = useCallback(async () => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return
    }

    if (!selectedHeldEvent?.id) return

    try {
      const res = await apiFetch(`${API_BASE}/appointments/confirm-held`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          appointmentId: selectedHeldEvent.id
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo confirmar la reserva")
        return
      }

      alert("Reserva confirmada")
      setSelectedHeldEvent(null)
      await refreshAgendaView()
    } catch {
      alert("Error al confirmar reserva")
    }
  }, [
    isReadOnly,
    readOnlyReason,
    API_BASE,
    selectedHeldEvent,
    setSelectedHeldEvent,
    refreshAgendaView
  ])

  const cancelHeld = useCallback(async () => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return
    }

    if (!selectedHeldEvent?.id) return

    try {
      const res = await apiFetch(`${API_BASE}/appointments/cancel-held`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          appointmentId: selectedHeldEvent.id
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo cancelar la reserva")
        return
      }

      alert("Reserva cancelada")
      setSelectedHeldEvent(null)
      await refreshAgendaView()
    } catch {
      alert("Error al cancelar reserva")
    }
  }, [
    isReadOnly,
    readOnlyReason,
    API_BASE,
    selectedHeldEvent,
    setSelectedHeldEvent,
    refreshAgendaView
  ])



  const updateSelectedAppointment = useCallback(async (payload = {}) => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return false
    }

    const appointmentId = payload.appointmentId || selectedAppointment?.id

    if (!appointmentId) {
      alert("No hay cita seleccionada")
      return false
    }

    try {
      setSaving(true)

      const targetClinicId =
        payload.clinicId ||
        selectedAppointment?.clinicId ||
        selectedAppointment?.extendedProps?.clinicId ||
        clinicId

      const res = await apiFetch(`${API_BASE}/appointments/update-details`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...payload,
          appointmentId,
          clinicId: targetClinicId
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo actualizar la cita")
        return false
      }

      const updatedAppointment = data?.appointment || data

      if (typeof setSelectedAppointment === "function" && updatedAppointment?.id) {
        setSelectedAppointment(updatedAppointment)
      }

      if (payload.date && typeof setDate === "function") {
        setDate(payload.date)
      }

      await refreshAgendaView(targetClinicId, payload.date || date)

      alert("Cita actualizada correctamente")
      return true
    } catch {
      alert("Error al actualizar la cita")
      return false
    } finally {
      setSaving(false)
    }
  }, [
    isReadOnly,
    readOnlyReason,
    API_BASE,
    selectedAppointment,
    clinicId,
    setSaving,
    setSelectedAppointment,
    setDate,
    refreshAgendaView,
    date
  ])


  
  const cancelSelectedAppointment = useCallback(async () => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return false
    }

    if (!selectedAppointment?.id) {
      alert("No hay cita seleccionada")
      return false
    }

    const status = String(selectedAppointment?.status || selectedAppointment?.extendedProps?.status || "").toUpperCase()

    if (["COMPLETED", "FINISHED"].includes(status)) {
      alert("No se puede cancelar una cita ya finalizada. Revisa el cierre clínico/financiero.")
      return false
    }

    if (status === "CANCELLED") {
      alert("La cita ya está cancelada")
      return false
    }

    const ok = window.confirm(
      "¿Cancelar esta cita?\n\nLa cita saldrá de la agenda activa, pero quedará conservada en el historial del paciente."
    )

    if (!ok) return false

    const targetClinicId =
      selectedAppointment?.clinicId ||
      selectedAppointment?.extendedProps?.clinicId ||
      clinicId

    try {
      const res = await apiFetch(`${API_BASE}/appointments/cancel-appointment`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          appointmentId: selectedAppointment.id,
          clinicId: targetClinicId,
          reason: "Cancelada desde agenda"
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo cancelar la cita")
        return false
      }

      alert("Cita cancelada correctamente")
      setSelectedAppointment?.(null)
      await refreshAgendaView(targetClinicId, date)

      return true
    } catch {
      alert("Error al cancelar la cita")
      return false
    }
  }, [
    isReadOnly,
    readOnlyReason,
    selectedAppointment,
    clinicId,
    API_BASE,
    setSelectedAppointment,
    refreshAgendaView,
    date
  ])

  const deleteSelectedAppointment = useCallback(async () => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return false
    }

    if (!selectedAppointment?.id) {
      alert("No hay cita seleccionada")
      return false
    }

    const confirmation = window.prompt(
      "Esta acción eliminará la cita de forma permanente.\n\nÚsalo solo si fue un error de captura. Para confirmar escribe: ELIMINAR"
    )

    if (confirmation !== "ELIMINAR") return false

    const targetClinicId =
      selectedAppointment?.clinicId ||
      selectedAppointment?.extendedProps?.clinicId ||
      clinicId

    const params = new URLSearchParams()

    if (targetClinicId) {
      params.set("clinicId", targetClinicId)
    }

    try {
      const res = await apiFetch(
        `${API_BASE}/appointments/${selectedAppointment.id}${params.toString() ? `?${params.toString()}` : ""}`,
        {
          method: "DELETE"
        }
      )

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo eliminar la cita")
        return false
      }

      alert("Cita eliminada correctamente")
      setSelectedAppointment?.(null)
      await refreshAgendaView(targetClinicId, date)

      return true
    } catch {
      alert("Error al eliminar la cita")
      return false
    }
  }, [
    isReadOnly,
    readOnlyReason,
    selectedAppointment,
    clinicId,
    API_BASE,
    setSelectedAppointment,
    refreshAgendaView,
    date
  ])

  return {
    createAppointment,
    holdAppointment,
    confirmHeld,
    cancelHeld,
    updateSelectedAppointment,
    cancelSelectedAppointment,
    deleteSelectedAppointment,
    refreshAgendaView
  }
}
